import AlgoliaRenderer from '../../renderer/algoliaRenderer';
import AlgoliaAjaxActions from '../../algoliaAjaxActions';
import AlgoliaUtils from '../../algoliaUtils';

class AlgoliaEventListener {
    constructor() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', this.init.bind(this), false);
        } else {
            this.init();
        }
    }

    // reflects whether a submit would actually be allowed (see the 'submit' listener below)
    updateSubmitState() {
        const searchForm = document.querySelector('.js-gs-resultpage-searchform');
        const searchInput = document.querySelector('.js-gs-resultpage-searchinput');
        const btnSubmitSearch = document.querySelector('.js-gs-resultpage-submit');

        if (!searchForm || !searchInput || !btnSubmitSearch) {
            return;
        }

        const currentPageOnlyChecked = searchForm.querySelector('.js-gs-searchform--filter-checkbox:checked');

        btnSubmitSearch.disabled = !AlgoliaUtils.hasRealQuery(searchInput.value) && !currentPageOnlyChecked;
    }

    async init() {
        // attached regardless of location.search, otherwise a page load without a query
        // (e.g. after switching the language, which drops q/facetFilters) leaves the
        // facets/pagination controls rendered but without any event listeners forever
        this.listenForInteraction();

        // this script is loaded site-wide (the overlay can be opened from any page), so this
        // must not run outside the actual search results page just because the URL has a query string
        if (!AlgoliaUtils.isOnSearchResultsPage()) {
            return;
        }

        if (location.search) {
            const params = new URLSearchParams(location.search);
            const q = params.get('q') || '';
            let page = Number(params.get('page')) || 0;
            // const searchCurrentPage = params.get('current-page-only');
            // const searchFacets = params.get('facetFields');

            /*
            if (searchCurrentPage !== null) {
                searchFacets.push(`'package:${searchCurrentPage}'`);
            }
            */

            // mirror the search input's guard: an empty/invalid "q" (e.g. a bookmarked/shared
            // "?q=" link) must not reach Algolia unless facets/page were deliberately requested too.
            // Just sync the checkbox state (not .click(), which would fire the change listener
            // and still trigger a "match everything" request) and leave the results area empty.
            if (!AlgoliaUtils.hasRealQuery(q) && !params.has('facetFilters') && !params.has('page')) {
                const facetAllCheckbox = document.querySelector('#facetall');

                if (facetAllCheckbox) {
                    facetAllCheckbox.checked = true;
                }

                return;
            }

            AlgoliaUtils.applyFacetsFromLocation();

            let ajaxResults = await AlgoliaAjaxActions.performAjaxSearch(page);

            // q/facetFilters and page come from the URL independently, so the requested page
            // can be out of range for the filtered result set
            const nbPages = Number(ajaxResults.results[0].nbPages) || 0;
            if (page > 0 && page >= nbPages) {
                page = 0;
                AlgoliaUtils.updatePageParam(page);
                ajaxResults = await AlgoliaAjaxActions.performAjaxSearch(page);
            }

            // document.querySelector(this.class.searchInput).value = q;
            AlgoliaRenderer.renderSearchResultsFacets(ajaxResults);
            AlgoliaRenderer.renderResultsCount(ajaxResults);
            AlgoliaRenderer.renderPagination(ajaxResults);
            AlgoliaRenderer.renderPageCount(ajaxResults);
            AlgoliaRenderer.renderSearchResults(ajaxResults);

            // the input value above was just set programmatically (no 'input' event fired)
            this.updateSubmitState();
        } else {
            // no query yet (e.g. right after a language switch drops q/facetFilters) - sync the "all"
            // facet checkbox state only (not .click(), which would trigger an unwanted empty-query request)
            const facetAllCheckbox = document.querySelector('#facetall');

            if (facetAllCheckbox) {
                facetAllCheckbox.checked = true;
            }
        }
    }

    listenForInteraction() {
        // basic elements
        // elements inside the overlay
        // const searchForm = document.querySelector(this.class.searchForm);
        // const searchInput = document.querySelector(this.class.searchInput);
        const filtersList = document.querySelector('.js-gs-filters-list');
        const pagination = document.querySelector('.js-gs-pagination');
        const searchForm = document.querySelector('.js-gs-resultpage-searchform');
        const searchInput = document.querySelector('.js-gs-resultpage-searchinput');

        // block the native GET submit for an empty/invalid query so it never
        // reaches Algolia with a "match everything" request (see algoliaQuickResultsAction)
        if (searchForm && searchInput) {
            this.updateSubmitState();
            searchInput.addEventListener('input', () => this.updateSubmitState());
            searchForm.addEventListener('change', () => this.updateSubmitState());
            // the 'reset' event fires before the browser actually clears the field values
            searchForm.addEventListener('reset', () => setTimeout(() => this.updateSubmitState(), 0));

            searchForm.addEventListener('submit', (Event) => {
                const currentPageOnly = searchForm.querySelector('.js-gs-searchform--filter-checkbox:checked');

                if (!AlgoliaUtils.hasRealQuery(searchInput.value) && !currentPageOnly) {
                    Event.preventDefault();
                }
            });
        }

        if (pagination) {
            pagination.addEventListener('click', async (Event) => {
                Event.preventDefault();
                const params = new URLSearchParams(location.search);
                const totalPagesEl = document.querySelector('.js-total-pages');
                const nbPages = Number((totalPagesEl && totalPagesEl.textContent) || 0);

                let page = Number(params.get('page') || 0);

                if (Event.target.closest('.js-gs-paging-page')) {
                    page = Number(Event.target.closest('.js-gs-paging-page').dataset.page);
                } else if (Event.target.closest('.js-gs-paging-previous')) {
                    page = Math.max(0, page - 1);
                } else if (Event.target.closest('.js-gs-paging-next')) {
                    page = Math.min(nbPages - 1, page + 1);
                } else if (Event.target.closest('.js-gs-paging-first')) {
                    page = 0;
                } else if (Event.target.closest('.js-gs-paging-last')) {
                    page = nbPages - 1;
                } else {
                    return;
                }

                AlgoliaUtils.updatePageParam(page);

                const ajaxResults = await AlgoliaAjaxActions.performAjaxSearch(page);

                AlgoliaRenderer.renderSearchResultsFacets(ajaxResults);
                AlgoliaRenderer.renderResultsCount(ajaxResults);
                AlgoliaRenderer.renderPagination(ajaxResults);
                AlgoliaRenderer.renderPageCount(ajaxResults);
                AlgoliaRenderer.renderSearchResults(ajaxResults);
                window.scrollTo(0, 0);
            });
        }

        if (filtersList) {
            // labels are focusable via tabindex but, unlike native form controls, don't
            // react to Space/Enter on their own
            filtersList.addEventListener('keydown', (event) => {
                if (event.key !== ' ' && event.key !== 'Enter') {
                    return;
                }

                const label = event.target.closest('label');

                if (!label) {
                    return;
                }

                event.preventDefault();
                label.click();
            });

            filtersList.addEventListener('change', async (Event) => {
                const changedCheckbox = Event.target;

                if (!(changedCheckbox instanceof HTMLInputElement)) {
                    return;
                }

                if (changedCheckbox.type !== 'checkbox' || !changedCheckbox.checked) {
                    filtersList.querySelector('#facetall').click(); // select the "all" checkbox
                    filtersList.querySelector('label[for="facetall"]').focus();
                    return;
                }

                // uncheck all other checkboxes except the one that was just checked
                const otherCheckboxes = Array.from(filtersList.querySelectorAll('input[type="checkbox"]')).filter((checkbox) => checkbox !== changedCheckbox);
                otherCheckboxes.forEach((_, index) => {
                    otherCheckboxes[index].checked = false;
                });

                // changing a facet invalidates the current result set, so pagination must restart at page 0
                const page = 0;
                AlgoliaUtils.updatePageParam(page);

                const ajaxResults = await AlgoliaAjaxActions.performAjaxSearch(page);

                AlgoliaRenderer.renderSearchResultsFacets(ajaxResults);
                AlgoliaRenderer.renderResultsCount(ajaxResults);
                AlgoliaRenderer.renderPagination(ajaxResults);
                AlgoliaRenderer.renderPageCount(ajaxResults);
                AlgoliaRenderer.renderSearchResults(ajaxResults);
            });
        }
    }
}

export default new AlgoliaEventListener();
